import styled, { css } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken, Size } from '@optimus-web/common';
import { ANIMATION_CURVE, ANIMATION_DURATION } from '@optimus-web/local-tokens';
import { Avatar } from '../Avatar';
import { Divider } from '../Divider';
import { Icon } from '../Icon';
import { Stack } from '../Stack';

export const MenuListGroup = styled.div`
    color: ${getToken(TokenName.TEXT_STATIC_TERTIARY)};
    ${getTextTokenPreset('BODY_S_STRONG')};
    text-transform: uppercase;
    padding: ${getToken(TokenName.SPACING_100)}px ${getToken(TokenName.SPACING_200)}px;
    display: flex;
    gap: ${getToken(TokenName.SPACING_100)}px;
`;

export const MenuListGroupSeparator = styled(Divider)`
    padding: ${getToken(TokenName.SPACING_200)}px ${getToken(TokenName.SPACING_0)} ${getToken(TokenName.SPACING_25)}px;
`;

export const MenuListElement = styled.menu`
    & & {
        margin-top: ${getToken(TokenName.SPACING_100)}px;
    }

    margin: ${getToken(TokenName.SPACING_0)};
    padding: ${getToken(TokenName.SPACING_0)};
    list-style: none;
`;

export const MenuListItemElement = styled.li<{ collapsed?: boolean }>`
    + li {
        margin-top: ${getToken(TokenName.SPACING_100)}px;
    }

    ${MenuListElement} {
        ${({ collapsed, theme }) => !collapsed && `padding-left: ${getToken(TokenName.SPACING_450)({ theme })}px;`};
    }
`;

const COLOR_TRANSITION = css`
    transition-property: background-color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
`;

export const MenuListButton = styled.button<{
    active?: boolean;
    collapsed?: boolean;
    size?: Omit<Size, 'small'>;
}>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;
    width: 100%;
    border: none;
    background: none;
    cursor: pointer;
    box-sizing: border-box;
    text-align: left;
    margin: ${getToken(TokenName.SPACING_0)};
    border-radius: ${getToken(TokenName.BORDER_RADIUS_100)}px;
    padding: ${({ size, collapsed, theme }) =>
        size === 'large' && !collapsed
            ? `${getToken(TokenName.SPACING_150)({ theme })}px`
            : `${getToken(TokenName.SPACING_100)({ theme })}px`};
    ${({ collapsed, theme }) =>
        collapsed
            ? `${getToken(TokenName.SPACING_100)({ theme })}px`
            : `${getToken(TokenName.SPACING_200)({ theme })}px`};

    ${({ active, theme }) =>
        active
            ? `background-color: ${getToken(TokenName.BACKGROUND_INTERACTIVE_SECONDARY_DEFAULT)({ theme })};`
            : `&:hover {background-color: ${getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_HOVER)({
                  theme,
              })}}
              &:active {background-color: ${getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_ACTIVE)({
                  theme,
              })}}`};

    ${COLOR_TRANSITION}
`;

export const MenuListContent = styled(Stack)`
    gap: ${getToken(TokenName.SPACING_150)}px;
`;

export const MenuListAvatar = styled(Avatar)`
    & > div {
        width: ${getToken(TokenName.SPACING_300)}px;
        height: ${getToken(TokenName.SPACING_300)}px;
    }
`;

export const MenuListText = styled.div`
    flex: 1;
`;

export const MenuListTextTitle = styled.div`
    ${getTextTokenPreset('BODY_M_STRONG')};
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
`;

export const MenuListSubtitle = styled.div`
    ${getTextTokenPreset('BODY_S_STRONG')};
    color: ${getToken(TokenName.TEXT_STATIC_TERTIARY)};
`;

export const MenuListExpandIcon = styled(Icon)<{ expanded: boolean }>`
    transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'none')};
    transform-origin: center center;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
    transition-property: transform;
    position: relative;
    top: ${({ expanded }) => (expanded ? '2px' : 0)};
    margin-left: ${getToken(TokenName.SPACING_150)}px;
`;

export const MenuListGroupDivider = styled(Divider)`
    padding: ${getToken(TokenName.SPACING_0)}px ${getToken(TokenName.SPACING_150)}px;
`;
